import {
  AICoachLevelVocabularyLevels,
  UpsertAICoachLevelVocabularyPayload,
} from "../entities/ai-coach-level-vocabulary.entity";

export const AI_COACH_LEVELS = ["A1", "A2", "B1", "B2", "C1", "C2"] as const;

type AICoachLevel = (typeof AI_COACH_LEVELS)[number];

const topLevelPattern = /^([A-Za-z0-9_-]+):\s*$/;
const levelLinePattern = /^\s+([A-Za-z0-9_-]+):\s*(.*?)\s*$/;

export const createEmptyLevelVocabularyLevels = (): AICoachLevelVocabularyLevels => ({
  A1: "",
  A2: "",
  B1: "",
  B2: "",
  C1: "",
  C2: "",
});

export const normalizeLevelVocabularyValue = (value: string): string => value.trim();

export const normalizeLangCode = (langCode: string): string => langCode.trim().toLowerCase();

export const sanitizeLevelVocabularyLevels = (
  levels: AICoachLevelVocabularyLevels
): AICoachLevelVocabularyLevels => ({
  A1: normalizeLevelVocabularyValue(levels.A1),
  A2: normalizeLevelVocabularyValue(levels.A2),
  B1: normalizeLevelVocabularyValue(levels.B1),
  B2: normalizeLevelVocabularyValue(levels.B2),
  C1: normalizeLevelVocabularyValue(levels.C1),
  C2: normalizeLevelVocabularyValue(levels.C2),
});

const isPlainObject = (value: unknown): value is Record<string, unknown> =>
  typeof value === "object" && value !== null && !Array.isArray(value);

export const validateLevelVocabularyLevels = (value: unknown): string | null => {
  if (!isPlainObject(value)) {
    return "Levels must be an object with A1 to C2 keys.";
  }

  const keys = Object.keys(value);
  const unexpectedKeys = keys.filter((key) => !AI_COACH_LEVELS.includes(key as AICoachLevel));
  if (unexpectedKeys.length > 0) {
    return `Unexpected level keys: ${unexpectedKeys.join(", ")}.`;
  }

  const missingKeys = AI_COACH_LEVELS.filter((level) => !(level in value));
  if (missingKeys.length > 0) {
    return `Missing level keys: ${missingKeys.join(", ")}.`;
  }

  for (const level of AI_COACH_LEVELS) {
    if (typeof value[level] !== "string") {
      return `Level ${level} must be a string.`;
    }
  }

  return null;
};

export const validateLevelVocabularyPayload = (
  payload: Partial<UpsertAICoachLevelVocabularyPayload> | undefined | null
): string | null => {
  if (!payload) {
    return "Payload is required.";
  }

  const normalizedLangCode = normalizeLangCode(payload.langCode || "");
  if (!normalizedLangCode) {
    return "Language code is required.";
  }

  return validateLevelVocabularyLevels(payload.levels);
};

const unquoteYamlScalar = (value: string): string => {
  const trimmed = value.trim();

  if (!trimmed) return "";

  if (
    (trimmed.startsWith('"') && trimmed.endsWith('"')) ||
    (trimmed.startsWith("'") && trimmed.endsWith("'"))
  ) {
    const quote = trimmed[0];
    const inner = trimmed.slice(1, -1);

    return quote === '"'
      ? inner.replace(/\\"/g, '"').replace(/\\\\/g, "\\")
      : inner.replace(/\\'/g, "'").replace(/\\\\/g, "\\");
  }

  return trimmed;
};

export const parseLevelVocabularyYaml = (
  text: string
): UpsertAICoachLevelVocabularyPayload[] => {
  const lines = text.replace(/\r\n/g, "\n").split("\n");
  const records = new Map<string, AICoachLevelVocabularyLevels>();
  let currentLangCode: string | null = null;

  for (const rawLine of lines) {
    const trimmed = rawLine.trim();

    if (!trimmed || trimmed.startsWith("#")) continue;

    const topLevelMatch = rawLine.match(topLevelPattern);
    if (topLevelMatch && !rawLine.startsWith(" ")) {
      currentLangCode = normalizeLangCode(topLevelMatch[1]);
      if (!currentLangCode) {
        throw new Error("Encountered a language block without a valid language code.");
      }
      if (!records.has(currentLangCode)) {
        records.set(currentLangCode, createEmptyLevelVocabularyLevels());
      }
      continue;
    }

    const levelMatch = rawLine.match(levelLinePattern);
    if (levelMatch) {
      if (!currentLangCode) {
        throw new Error("Encountered a CEFR level before declaring a language code.");
      }

      const levelKey = levelMatch[1].trim();
      if (!AI_COACH_LEVELS.includes(levelKey as AICoachLevel)) {
        throw new Error(`Unexpected level key "${levelKey}" in language "${currentLangCode}".`);
      }

      const currentLevels = records.get(currentLangCode) || createEmptyLevelVocabularyLevels();
      currentLevels[levelKey as AICoachLevel] = normalizeLevelVocabularyValue(
        unquoteYamlScalar(levelMatch[2] || "")
      );
      records.set(currentLangCode, currentLevels);
      continue;
    }

    throw new Error(`Unable to parse line: ${rawLine}`);
  }

  const parsedRecords = Array.from(records.entries()).map(([langCode, levels]) => ({
    langCode,
    levels: sanitizeLevelVocabularyLevels(levels),
  }));

  if (!parsedRecords.length) {
    throw new Error("No valid language vocabulary configurations were found in the YAML file.");
  }

  return parsedRecords;
};

export const buildVocabularyPreview = (levels: AICoachLevelVocabularyLevels): string => {
  const firstPopulatedLevel = AI_COACH_LEVELS.find((level) => levels[level].trim());
  if (!firstPopulatedLevel) {
    return "No vocabulary configured yet.";
  }

  const preview = levels[firstPopulatedLevel].replace(/\s+/g, " ").trim();
  return `${firstPopulatedLevel}: ${preview.slice(0, 120)}${preview.length > 120 ? "..." : ""}`;
};
